import { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { OK, ERROR } from "helpers/data-statuses"
import { isUuid } from "helpers/types/uuid"
import { useAppStore } from "hooks/redux"
import { useIsMultiDashboardsEnabled } from "resources/account"
import { useReadPreference, useReadContextPreference, useSavePreference } from "hooks/preferences"
import { useCreateLayout } from "resources/layouts"
import { useCreateInstance, useSaveInstanceWorkspace, useDashboardInstances, DASHBOARD_INSTANCES_RESOURCE, type DashboardInstance } from "resources/dashboard-instances"
import { Creators as DataCreators } from "model/data"
import { warn } from "helpers/dev-logger"
import type { WorkspaceSerializableState } from "../layouts/serialize-workspace"
import {
  migrateDashboardToInstance,
  claimMigration,
  releaseMigration,
} from "../layouts/lazy-migration"

// Records that a dashboard-key was migrated — the only preference the instance path writes (APP-2705 carve-out).
export const MIGRATED_PREFERENCE_TYPE = "dashboard.migrated"

// Stable empty fallback so an unsettled/empty instances list doesn't churn the staleness check.
const EMPTY_INSTANCES: DashboardInstance[] = []

interface MigratedTarget { instanceId: string, slug: string }

export type LazyMigrationResult =
  | { status: "pending" }
  | { status: "migrated", instanceId: string, slug: string }
  | { status: "skip" }

// Narrow the persisted flag row field-by-field so a malformed value falls through to null, not the
// migrated branch. slug is optional for back-compat: a pre-slug flag routes by instanceId.
function readMigratedTarget(value: unknown): MigratedTarget | null {
  if (!value || typeof value !== "object" || !("instanceId" in value)) return null
  const instanceId = (value as { instanceId: unknown }).instanceId
  if (typeof instanceId !== "string" || instanceId.length === 0) return null
  const rawSlug = (value as { slug?: unknown }).slug
  const slug = typeof rawSlug === "string" && rawSlug.length > 0 ? rawSlug : instanceId
  return { instanceId, slug }
}

// The dashboard.model preference is { modelId }. Narrow field-by-field so a malformed row falls through to null.
function readModelId(value: unknown): string | null {
  if (value && typeof value === "object" && "modelId" in value) {
    const modelId = (value as { modelId: unknown }).modelId
    return typeof modelId === "string" && modelId.length > 0 ? modelId : null
  }
  return null
}

/**
 * Imports a legacy dashboard (the "default" one or any preference-backed slug dashboard) as a
 * layout + instance on the first flag-on load (APP-3155); a real instance UUID short-circuits to `skip`.
 *
 * Idempotent per dashboard-key via the dashboard.migrated preference row AND a module-level run-once
 * guard. The flag is reconciled against the live instances list: one naming an instance that no
 * longer exists is re-migrated, never trusted into a route-to-a-dead-instance loop (APP-3235).
 * Flag-off returns `skip` without reading the flag or touching any preference.
 *
 * @returns a discriminated result the layout branches on:
 *   - `pending`  — flag read not settled, or migration in flight (render <Loading/>).
 *   - `migrated` — already migrated or just migrated; route to `instanceId`.
 *   - `skip`     — flag off, or this id is already a real instance (don't migrate).
 */
export function useLazyMigration(dashboardId: string): LazyMigrationResult {
  const isInstanceMode = useIsMultiDashboardsEnabled()
  // Any non-UUID id migrates (the "default" sentinel and legacy slug dashboards); a UUID is already a real instance.
  const isMigratable = isInstanceMode && !isUuid(dashboardId)

  const store = useAppStore()
  const dispatch = useDispatch()
  const createLayout = useCreateLayout()
  const createInstance = useCreateInstance()
  const saveWorkspace = useSaveInstanceWorkspace()
  const saveMigratedFlag = useSavePreference(dashboardId, MIGRATED_PREFERENCE_TYPE)

  // Reconcile the migrated flag against the live list (APP-3235): a flag naming a gone instance must
  // not be trusted, or we route to a dead instance forever. Gated on isMigratable so flag-off makes no request.
  const [instances, instancesStatus] = useDashboardInstances(isMigratable)

  // Gated on isMigratable so flag-off makes NO request (must be pre-migration behavior byte-for-byte).
  const [flagValue, flagStatus] = useReadPreference(dashboardId, MIGRATED_PREFERENCE_TYPE, null, { enabled: isMigratable })
  // Read arrangement + model directly from preference rows, NOT slice/context: migration fires when the
  // flag settles, possibly before the dockviewLayout query / model slice land — state would snapshot null.
  // Gated on isMigratable. null → panels-only / no saved model.
  const [arrangement, arrangementStatus] = useReadContextPreference("dockviewLayout", null, { enabled: isMigratable })
  const [modelValue, modelStatus] = useReadContextPreference("model", null, { enabled: isMigratable })

  const [migratedTarget, setMigratedTarget] = useState<MigratedTarget | null>(null)
  // On POST reject, flip this so the gate stops waiting on <Loading/> (the effect won't re-run with
  // unchanged deps) and falls through to `skip`; the next load retries.
  const [migrationFailed, setMigrationFailed] = useState(false)
  // A successful migration leaves its run-once claim in place; a later stale-flag re-migration must
  // release it once or claimMigration deadlocks the gate (APP-3235). The ref keeps the release
  // idempotent across a StrictMode double-invoke so it never clears a freshly-acquired claim.
  const staleClaimReleasedRef = useRef(false)

  const existingTarget = readMigratedTarget(flagValue)
  // String (not the object) for the effect guard + deps — a fresh object ref would churn the effect.
  const existingInstanceId = existingTarget?.instanceId ?? null
  const flaggedInstanceExists = existingInstanceId != null
    && (instances ?? EMPTY_INSTANCES).some((instance) => instance.instanceId === existingInstanceId)
  // Stale only when an OK list lacks the named instance — an ERROR fetch can't prove absence, and
  // treating it as stale would re-migrate a still-valid instance into a duplicate on a transient blip.
  const isStaleFlag = existingTarget != null && instancesStatus === OK && !flaggedInstanceExists
  const persistedModelId = readModelId(modelValue)
  // Accept ERROR too, or the gate hangs forever: on read ERROR, treat as "no row" and migrate.
  const flagSettled = flagStatus === OK || flagStatus === ERROR
  // Don't serialize until the layout + model reads have resolved, or the snapshot captures nulls.
  const prefsSettled = (arrangementStatus === OK || arrangementStatus === ERROR)
    && (modelStatus === OK || modelStatus === ERROR)

  useEffect(() => {
    if (!isMigratable || !flagSettled || !prefsSettled) return
    // A present, still-valid flag means already-migrated. A STALE flag (instance gone) falls through to re-migrate (APP-3235).
    if (existingInstanceId && !isStaleFlag) return
    // Re-migrating a stale flag: release the prior (now-void) claim once so the claim below can succeed.
    if (isStaleFlag && !staleClaimReleasedRef.current) {
      staleClaimReleasedRef.current = true
      releaseMigration(dashboardId)
    }
    // Claim synchronously (before any await) so a concurrent mount / StrictMode double-invoke can't
    // also start. The module-level Set is the run-once guard (a React ref re-arms per remount);
    // released on failure below so a transient error can retry.
    if (!claimMigration(dashboardId)) return

    // Trust cast (ADR 0003): verbatim server-validated Dockview toJSON blob. null → panels-only.
    const arrangementBlob = arrangement as Record<string, unknown> | null
    // Read FRESH state in the callback (never a render-closure snapshot).
    const state = store.getState() as unknown as WorkspaceSerializableState

    migrateDashboardToInstance({ state, dashboardId, arrangement: arrangementBlob, modelId: persistedModelId, createLayout, createInstance, saveWorkspace })
      .then(({ instanceId, slug }) => {
        // Fire-and-forget flag write (the instance exists; the row just prevents re-migration). If
        // lost AND the page reloads (guard resets), the migration repeats, but createInstance is
        // keyed on dashboardId server-side (APP-3261) so the repeat returns this same instance
        // rather than minting a duplicate.
        saveMigratedFlag({ instanceId, slug })
        // The list predates this POST, so reset the cache to force a refetch — else routing redirects the new id as "unknown".
        dispatch(DataCreators.resetCollections([DASHBOARD_INSTANCES_RESOURCE]))
        setMigratedTarget({ instanceId, slug })
      })
      .catch((error: unknown) => {
        // Release the key so a transient failure doesn't wedge it; flip migrationFailed so the gate
        // renders the legacy dashboard. Next load retries (guard released + fresh mount).
        warn("Lazy dashboard migration failed", error)
        releaseMigration(dashboardId)
        setMigrationFailed(true)
      })
  }, [isMigratable, flagSettled, prefsSettled, existingInstanceId, isStaleFlag, dashboardId, arrangement, persistedModelId, store, dispatch, createLayout, createInstance, saveWorkspace, saveMigratedFlag])

  if (!isMigratable) return { status: "skip" }
  // A just-completed (re)migration wins over a stale flag still in the preference cache.
  if (migratedTarget) return { status: "migrated", ...migratedTarget }
  // Trust the flag only when the list confirms the target exists (OK + present), or it errored so
  // absence can't be proven. While loading or stale, fall through to pending so we never navigate to
  // a not-yet-confirmed (possibly dead) slug (APP-3235).
  if (existingTarget && (flaggedInstanceExists || instancesStatus === ERROR)) {
    return { status: "migrated", ...existingTarget }
  }
  // Migration failed — render the legacy dashboard instead of hanging (retry on next load).
  if (migrationFailed) return { status: "skip" }
  // Flag unsettled, or settled-with-no-row/stale while the migration runs: stay pending until the new id arrives.
  return { status: "pending" }
}
